import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AUTO_DISMISS_MS,
  ResourceNameLink,
  useToast,
} from '@spotify/confidence-core-react';

import { usePulseBlock } from '../../hooks/usePulseBlock';
import { isPulseBlockError, isPulseBlockPending } from '../../utils';

type TrackedBlock = {
  pulseName: string;
  blockName: string;
};

export type CapturedBlock = TrackedBlock & {
  pulseDisplayName?: string | null;
  blockDisplayName: string;
};

export type PulseCaptureState = 'added' | 'adding' | 'failed' | 'persisted';

export type PulseCaptureResult = {
  captures: CapturedBlock[];
  eventName?: string;
  state: 'failed' | 'pending' | 'successful';
};

type CapturedBlockState = 'completed' | 'failed' | 'pending';

type TrackedCapture = {
  attemptId: number;
  pulseName: string;
  blockName: string;
  notify: boolean;
  resultReported: boolean;
  state: CapturedBlockState;
};

type CompletedCapture = TrackedCapture & {
  state: Exclude<CapturedBlockState, 'pending'>;
};

type PulseCaptureAttempt = {
  fail: () => void;
  track: (capture?: TrackedBlock) => void;
};

type PulseCaptureNotificationsContextValue = {
  notifyPulseCreated: (pulseName: string) => void;
  observeCaptureResult: (result: PulseCaptureResult) => void;
  startBlockCapture: (pulseName: string) => PulseCaptureAttempt;
  captureStateByPulseName: ReadonlyMap<string, PulseCaptureState>;
};

const EMPTY_CAPTURE_STATES = new Map<string, PulseCaptureState>();

const getPulseToastTitle = ({
  prefix,
  pulseName,
  suffix = '',
}: {
  prefix: string;
  pulseName: string;
  suffix?: string;
}) => (
  <>
    {prefix}
    <ResourceNameLink name={pulseName} text="Pulse" />
    {suffix}
  </>
);

const PulseCaptureNotificationsContext =
  createContext<PulseCaptureNotificationsContextValue | null>(null);

const PulseBlockCaptureTracker = ({
  attemptId,
  blockName,
  onStateChange,
}: {
  attemptId: number;
  blockName: string;
  onStateChange: (attemptId: number, state: CapturedBlockState) => void;
}) => {
  const { pulseBlock, error, isLoading, isPollingTimedOut } = usePulseBlock({
    name: blockName,
  });
  let state: CapturedBlockState = 'completed';

  if (
    error ||
    isPollingTimedOut ||
    (pulseBlock && isPulseBlockError(pulseBlock))
  ) {
    state = 'failed';
  } else if (isLoading || !pulseBlock || isPulseBlockPending(pulseBlock)) {
    state = 'pending';
  }

  useEffect(() => {
    onStateChange(attemptId, state);
  }, [attemptId, onStateChange, state]);

  return null;
};

export const PulseCaptureNotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const toast = useToast();
  const [captures, setCaptures] = useState<TrackedCapture[]>([]);
  const nextAttemptId = useRef(0);
  const pendingCaptureEventNames = useRef(new Set<string>());
  const captureToasts = useRef(
    new Map<number, ReturnType<typeof toast.post>>(),
  );
  const completedCaptureTimeouts = useRef(
    new Map<number, ReturnType<typeof setTimeout>>(),
  );

  const closeCaptureToast = useCallback((attemptId: number) => {
    captureToasts.current.get(attemptId)?.close();
    captureToasts.current.delete(attemptId);
  }, []);

  const notifyPulseCreated = useCallback(
    (pulseName: string) => {
      toast.post({
        title: getPulseToastTitle({
          prefix: '',
          pulseName,
          suffix: ' created',
        }),
        status: 'success',
        timeout: AUTO_DISMISS_MS,
      });
    },
    [toast],
  );

  const postBlockResult = useCallback(
    (
      attemptId: number,
      pulseName: string,
      state: Exclude<CapturedBlockState, 'pending'>,
    ) => {
      closeCaptureToast(attemptId);
      toast.post({
        title: getPulseToastTitle({
          prefix:
            state === 'failed'
              ? 'Block failed to be added to '
              : 'Block added to ',
          pulseName,
        }),
        status: state === 'failed' ? 'danger' : 'success',
        ...(state === 'completed' ? { timeout: AUTO_DISMISS_MS } : {}),
      });
    },
    [closeCaptureToast, toast],
  );

  const startBlockCapture = useCallback(
    (pulseName: string): PulseCaptureAttempt => {
      const attemptId = nextAttemptId.current;
      nextAttemptId.current += 1;
      captureToasts.current.set(
        attemptId,
        toast.post({
          title: getPulseToastTitle({
            prefix: 'Block getting added to ',
            pulseName,
            suffix: '…',
          }),
          status: 'info',
        }),
      );

      let handled = false;
      return {
        fail: () => {
          if (handled) return;
          handled = true;
          postBlockResult(attemptId, pulseName, 'failed');
        },
        track: capture => {
          if (handled) return;
          handled = true;
          if (!capture) {
            postBlockResult(attemptId, pulseName, 'completed');
            return;
          }
          setCaptures(current => [
            ...current,
            {
              attemptId,
              pulseName: capture.pulseName,
              blockName: capture.blockName,
              notify: true,
              resultReported: false,
              state: 'pending',
            },
          ]);
        },
      };
    },
    [postBlockResult, toast],
  );

  const observeCaptureResult = useCallback((result: PulseCaptureResult) => {
    if (!result.eventName) return;

    if (result.state === 'pending') {
      pendingCaptureEventNames.current.add(result.eventName);
      return;
    }

    const wasPending = pendingCaptureEventNames.current.delete(
      result.eventName,
    );
    if (result.state !== 'successful' || !wasPending) return;

    setCaptures(current => {
      const existingBlockNames = new Set(
        current.map(capture => capture.blockName),
      );
      const newCaptures = result.captures.flatMap(capture => {
        if (existingBlockNames.has(capture.blockName)) return [];
        existingBlockNames.add(capture.blockName);
        const attemptId = nextAttemptId.current;
        nextAttemptId.current += 1;
        return [
          {
            attemptId,
            pulseName: capture.pulseName,
            blockName: capture.blockName,
            notify: false,
            resultReported: false,
            state: 'pending' as const,
          },
        ];
      });
      return newCaptures.length > 0 ? [...current, ...newCaptures] : current;
    });
  }, []);

  const updateCapturedBlockState = useCallback(
    (attemptId: number, state: CapturedBlockState) => {
      setCaptures(current =>
        current.map(tracked =>
          tracked.attemptId === attemptId && tracked.state !== state
            ? { ...tracked, state }
            : tracked,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    const completedCaptures = captures.filter(
      (capture): capture is CompletedCapture =>
        capture.state !== 'pending' && !capture.resultReported,
    );
    if (completedCaptures.length === 0) return;

    completedCaptures.forEach(({ attemptId, notify, pulseName, state }) => {
      if (notify) {
        postBlockResult(attemptId, pulseName, state);
      }
      if (state === 'completed') {
        completedCaptureTimeouts.current.set(
          attemptId,
          setTimeout(() => {
            setCaptures(current =>
              current.filter(capture => capture.attemptId !== attemptId),
            );
            completedCaptureTimeouts.current.delete(attemptId);
          }, AUTO_DISMISS_MS),
        );
      }
    });

    const reportedAttemptIds = new Set(
      completedCaptures.map(capture => capture.attemptId),
    );
    setCaptures(current =>
      current.map(capture =>
        reportedAttemptIds.has(capture.attemptId)
          ? { ...capture, resultReported: true }
          : capture,
      ),
    );
  }, [captures, postBlockResult]);

  const captureStateByPulseName = useMemo(() => {
    const states = new Map<string, PulseCaptureState>();

    captures.forEach(capture => {
      const pulseState = states.get(capture.pulseName);
      if (capture.state === 'pending') {
        states.set(capture.pulseName, 'adding');
      } else if (capture.state === 'failed' && pulseState !== 'adding') {
        states.set(capture.pulseName, 'failed');
      } else if (!pulseState) {
        states.set(capture.pulseName, 'added');
      }
    });

    return states;
  }, [captures]);

  useEffect(
    () => () => {
      captureToasts.current.forEach(captureToast => captureToast.close());
      captureToasts.current.clear();
      completedCaptureTimeouts.current.forEach(timeout =>
        clearTimeout(timeout),
      );
      completedCaptureTimeouts.current.clear();
    },
    [],
  );

  const value = useMemo<PulseCaptureNotificationsContextValue>(
    () => ({
      captureStateByPulseName,
      notifyPulseCreated,
      observeCaptureResult,
      startBlockCapture,
    }),
    [
      captureStateByPulseName,
      notifyPulseCreated,
      observeCaptureResult,
      startBlockCapture,
    ],
  );

  return (
    <PulseCaptureNotificationsContext.Provider value={value}>
      {children}
      {captures.map(({ attemptId, blockName, state }) =>
        state === 'pending' ? (
          <PulseBlockCaptureTracker
            key={attemptId}
            attemptId={attemptId}
            blockName={blockName}
            onStateChange={updateCapturedBlockState}
          />
        ) : null,
      )}
    </PulseCaptureNotificationsContext.Provider>
  );
};

export const usePulseCaptureNotifications =
  (): PulseCaptureNotificationsContextValue => {
    const value = useContext(PulseCaptureNotificationsContext);

    return useMemo(
      () =>
        value ?? {
          captureStateByPulseName: EMPTY_CAPTURE_STATES,
          notifyPulseCreated: () => {},
          observeCaptureResult: () => {},
          startBlockCapture: () => ({ fail: () => {}, track: () => {} }),
        },
      [value],
    );
  };

export const usePulseCaptureState = (pulseName: string): PulseCaptureState => {
  const value = useContext(PulseCaptureNotificationsContext);
  return value?.captureStateByPulseName.get(pulseName) ?? 'persisted';
};
